import { type UpdateActiveViewInput, type UpdateActiveViewMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateActiveViewMutationDocument } from "./active-view.gql";
import { EVENT_ACTIVE_VIEW_UPDATED, trackActiveViewEvent } from "./tracking";

type UpdatedActiveView = UpdateActiveViewMutation["updateActiveView"];

export interface UseUpdateActiveViewProps {
  onCompleted?: (data: UpdatedActiveView) => void;
  onError?: (e: Error) => void;
}

export const useUpdateActiveView = ({ onCompleted, onError }: UseUpdateActiveViewProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: UpdateActiveViewInput) => {
      const client = createOperationClient("UpdateActiveView");
      const data = await client.request(updateActiveViewMutationDocument, { input });
      return data.updateActiveView;
    },
    onSuccess: (updatedActiveView) => {
      trackActiveViewEvent(EVENT_ACTIVE_VIEW_UPDATED, updatedActiveView);

      queryClient.setQueryData(
        queryKeys.activeViews.detail(updatedActiveView.key, updatedActiveView.workspaceId),
        updatedActiveView,
      );

      // Invalidate active view list for the workspace
      queryClient.invalidateQueries({
        queryKey: queryKeys.activeViews.list(updatedActiveView.workspaceId),
      });

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(updatedActiveView);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    updateActiveView: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
